<template>
    <div class="token-markets-container">
        <!-- Liquidity Toggle -->
        <div class="liquidity-toggle-row" v-if="markets.length > 0">
            <div class="liquidity-summary">
                <span class="summary-label">Total:</span>
                <span class="summary-value">${{ formatCompact(displayedTotalLiquidity) }}</span>
            </div>
            <div class="toggle-container">
                <button
                    class="toggle-btn"
                    :class="{ active: !showSellSideOnly }"
                    @click="setLiquidityMode(false)"
                    title="Show total liquidity (both sides)"
                >
                    Total
                </button>
                <button
                    class="toggle-btn"
                    :class="{ active: showSellSideOnly }"
                    @click="setLiquidityMode(true)"
                    title="Show sell-side liquidity only (excluding this token)"
                >
                    Sell-Side
                </button>
            </div>
        </div>

        <div v-if="isLoading" class="loading-state">
            <div class="loading-spinner"></div>
            <span>Loading markets...</span>
        </div>
        <div v-else-if="error" class="error-state">{{ error }}</div>
        <div v-else-if="markets.length === 0" class="empty-state">
            No liquidity pools found for this token.
        </div>
        <table v-else class="markets-table">
            <thead>
                <tr>
                    <th class="col-exchange sortable" @click="toggleSort('exchange')">
                        Exchange
                        <span class="sort-icon" v-if="sortKey === 'exchange'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                    </th>
                    <th class="col-pair sortable" @click="toggleSort('pair')">
                        Pair
                        <span class="sort-icon" v-if="sortKey === 'pair'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                    </th>
                    <th class="col-liquidity sortable" @click="toggleSort('liquidity')">
                        Liquidity
                        <span class="sort-icon" v-if="sortKey === 'liquidity'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                    </th>
                    <th class="col-volume sortable" @click="toggleSort('volume')">
                        Vol 24h
                        <span class="sort-icon" v-if="sortKey === 'volume'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                    </th>
                    <th class="col-efficiency sortable" @click="toggleSort('efficiency')" title="24h Volume / Liquidity">
                        Vol/Liq
                        <span class="sort-icon" v-if="sortKey === 'efficiency'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="market in sortedMarkets" :key="market.address">
                    <td class="col-exchange">
                        <div class="exchange-cell">
                            <span class="exchange-dot" :class="'dot-' + getExchangeColor(market.program_id, market.protocol)"></span>
                            <span class="exchange-name">{{ getExchangeName(market.program_id, market.protocol) }}</span>
                        </div>
                    </td>
                    <td class="col-pair">
                        <div class="pair-cell">
                            <a :href="getPoolUrl(market.address, market.protocol)"
                               target="_blank"
                               class="pair-link"
                               :title="`Pool: ${market.address}`">
                                {{ market.base_token?.symbol || '?' }}/{{ market.quote_token?.symbol || '?' }}
                                <svg class="external-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.5 3C3.22386 3 3 3.22386 3 3.5V8.5C3 8.77614 3.22386 9 3.5 9H8.5C8.77614 9 9 8.77614 9 8.5V6.5M6.5 3H9M9 3V5.5M9 3L5 7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </a>
                            <span class="pair-type">{{ market.type }}</span>
                        </div>
                    </td>
                    <td class="col-liquidity">
                        <div class="metric-cell">
                            <div class="metric-bar-bg">
                                <div
                                    class="metric-bar-fill liquidity-fill"
                                    :style="{ width: getLiquidityPercent(getDisplayLiquidity(market)) + '%' }"
                                ></div>
                            </div>
                            <span class="metric-value liquidity-value">${{ formatCompact(getDisplayLiquidity(market)) }}</span>
                        </div>
                    </td>
                    <td class="col-volume">
                        <div class="metric-cell">
                            <div class="metric-bar-bg">
                                <div
                                    class="metric-bar-fill volume-fill"
                                    :style="{ width: getVolumePercent(market.volume_usd_24h) + '%' }"
                                ></div>
                            </div>
                            <span class="metric-value volume-value">${{ formatCompact(market.volume_usd_24h) }}</span>
                        </div>
                    </td>
                    <td class="col-efficiency">
                        <span class="efficiency-value" :class="getEfficiencyClass(market)">
                            {{ formatEfficiency(market) }}
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="markets-footer" v-if="markets.length > 0">
            <small class="text-muted">
                Showing {{ markets.length }} liquidity pools
            </small>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import axios from 'axios';

const props = defineProps({
    mint: {
        type: String,
        required: true,
    }
});

const markets = ref([]);
const isLoading = ref(false);
const error = ref(null);
const showSellSideOnly = ref(false);
const sortKey = ref('volume'); // Default sort by volume
const sortDir = ref('desc');   // Default descending
let intervalId = null;

// Storage key for user preference
const STORAGE_KEY = 'tokenMarkets_liquidityMode';

// Load preference from localStorage
const loadLiquidityPreference = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== null) {
            showSellSideOnly.value = stored === 'sell-side';
        }
    } catch (e) {
        // localStorage not available
    }
};

// Save preference to localStorage
const setLiquidityMode = (sellSideOnly) => {
    showSellSideOnly.value = sellSideOnly;
    try {
        localStorage.setItem(STORAGE_KEY, sellSideOnly ? 'sell-side' : 'total');
    } catch (e) {
        // localStorage not available
    }
};

// Known DEX programs with display names
const knownPrograms = {
    '675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8': { name: 'Raydium', color: 'purple' },
    'CAMMCzo5YL8w4VFF8KVHrK22GGUsp5VTaW7grrKgrWqK': { name: 'Raydium CLMM', color: 'purple' },
    'CPMMoo8L3F4NbTegBCKVNunggL7H1ZpdTHKxQB5qKP1C': { name: 'Raydium CPMM', color: 'purple' },
    'JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4': { name: 'Jupiter', color: 'green' },
    'whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc': { name: 'Orca', color: 'cyan' },
    '9W959DqEETiGZoccp2oneHNtP5tvBGKR7nq9ctL9dUfw': { name: 'Lifinity', color: 'blue' },
    'MERLuDFBMmsHnsBPZw2sDQZHvXFMwp8EdjudcU2HKky': { name: 'Mercurial', color: 'teal' },
    'Eo7WjKq67rjJQSZxS6z3YkapzY3eMj6Xy8X5EQVn5UaB': { name: 'Meteora', color: 'orange' },
    'LBUZKhRxPF3XUpBCjp4YzTKgLccjZhTSDM9YuVaPwxo': { name: 'Meteora DLMM', color: 'orange' },
};

// Protocol name mapping from pool-state index
const protocolNames = {
    'raydium_amm': { name: 'Raydium AMM', color: 'purple' },
    'raydium_clmm': { name: 'Raydium CLMM', color: 'purple' },
    'raydium_cpmm': { name: 'Raydium CPMM', color: 'purple' },
    'orca_whirlpool': { name: 'Orca', color: 'cyan' },
    'meteora_dlmm': { name: 'Meteora DLMM', color: 'orange' },
    'meteora_pools': { name: 'Meteora', color: 'orange' },
    'lifinity': { name: 'Lifinity', color: 'blue' },
};

const getExchangeName = (programId, protocol = null) => {
    if (protocol && protocolNames[protocol]) {
        return protocolNames[protocol].name;
    }
    return knownPrograms[programId]?.name || protocol || 'Unknown DEX';
};

const getExchangeColor = (programId, protocol = null) => {
    if (protocol && protocolNames[protocol]) {
        return protocolNames[protocol].color;
    }
    return knownPrograms[programId]?.color || 'gray';
};

// Generate direct link to pool on native platform
const getPoolUrl = (poolAddress, protocol) => {
    switch (protocol) {
        case 'orca_whirlpool':
            return `https://www.orca.so/pools/${poolAddress}`;
        case 'meteora_dlmm':
            return `https://app.meteora.ag/dlmm/${poolAddress}`;
        case 'meteora_pools':
            return `https://app.meteora.ag/pools/${poolAddress}`;
        case 'meteora_dammv2':
            return `https://app.meteora.ag/dammv2/${poolAddress}`;
        case 'raydium_clmm':
            return `https://raydium.io/clmm/create-position/?pool_id=${poolAddress}`;
        case 'raydium_amm':
        case 'raydium_cpmm':
            return `https://raydium.io/liquidity/increase/?mode=add&pool_id=${poolAddress}`;
        default:
            // Fallback to Solscan
            return `https://solscan.io/account/${poolAddress}`;
    }
};

// Get displayed liquidity based on toggle
const getDisplayLiquidity = (market) => {
    if (showSellSideOnly.value) {
        // Sell-side = quote token liquidity only (the other side of the pair)
        return market.quote_liquidity_usd || 0;
    }
    return market.liquidity_usd || 0;
};

// Calculate efficiency (volume/liquidity turnover)
const getEfficiency = (market) => {
    const liquidity = getDisplayLiquidity(market);
    if (!liquidity || liquidity === 0) return 0;
    return (market.volume_usd_24h || 0) / liquidity;
};

// Format efficiency as "0.1x" or "3.2x"
const formatEfficiency = (market) => {
    const eff = getEfficiency(market);
    if (eff === 0) return '0x';
    if (eff >= 10) return eff.toFixed(0) + 'x';
    if (eff >= 1) return eff.toFixed(1) + 'x';
    return eff.toFixed(2) + 'x';
};

// Get color class based on efficiency
const getEfficiencyClass = (market) => {
    const eff = getEfficiency(market);
    if (eff >= 1) return 'eff-high';
    if (eff >= 0.1) return 'eff-medium';
    return 'eff-low';
};

// Toggle sort on column click
const toggleSort = (key) => {
    if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortKey.value = key;
        sortDir.value = 'desc'; // Default to desc for new column
    }
};

// Sorted markets based on current sort key and direction
const sortedMarkets = computed(() => {
    const sorted = [...markets.value].sort((a, b) => {
        let aVal, bVal;

        switch (sortKey.value) {
            case 'exchange':
                aVal = getExchangeName(a.program_id, a.protocol).toLowerCase();
                bVal = getExchangeName(b.program_id, b.protocol).toLowerCase();
                return sortDir.value === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            case 'pair':
                aVal = `${a.base_token?.symbol || ''}/${a.quote_token?.symbol || ''}`.toLowerCase();
                bVal = `${b.base_token?.symbol || ''}/${b.quote_token?.symbol || ''}`.toLowerCase();
                return sortDir.value === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            case 'liquidity':
                aVal = getDisplayLiquidity(a);
                bVal = getDisplayLiquidity(b);
                break;
            case 'volume':
                aVal = a.volume_usd_24h || 0;
                bVal = b.volume_usd_24h || 0;
                break;
            case 'efficiency':
                aVal = getEfficiency(a);
                bVal = getEfficiency(b);
                break;
            default:
                return 0;
        }

        // Numeric sort
        return sortDir.value === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
});

// Calculate totals
const totalLiquidity = computed(() => {
    return markets.value.reduce((sum, m) => sum + (m.liquidity_usd || 0), 0);
});

const sellSideLiquidity = computed(() => {
    return markets.value.reduce((sum, m) => sum + (m.quote_liquidity_usd || 0), 0);
});

const displayedTotalLiquidity = computed(() => {
    return showSellSideOnly.value ? sellSideLiquidity.value : totalLiquidity.value;
});

// Max values for progress bars
const maxLiquidity = computed(() => {
    const values = markets.value.map(m => showSellSideOnly.value ? (m.quote_liquidity_usd || 0) : (m.liquidity_usd || 0));
    return Math.max(...values, 1);
});

const maxVolume = computed(() => {
    return Math.max(...markets.value.map(m => m.volume_usd_24h || 0), 1);
});

const getLiquidityPercent = (liquidity) => {
    return Math.min(100, (liquidity / maxLiquidity.value) * 100);
};

const getVolumePercent = (volume) => {
    return Math.min(100, (volume / maxVolume.value) * 100);
};

const formatCompact = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toFixed(0);
};

// Emit liquidity to header via custom event
const emitLiquidityUpdate = () => {
    window.dispatchEvent(new CustomEvent('tokenLiquidityUpdate', {
        detail: {
            mint: props.mint,
            totalLiquidity: totalLiquidity.value,
            sellSideLiquidity: sellSideLiquidity.value,
            showSellSideOnly: showSellSideOnly.value,
            displayedLiquidity: displayedTotalLiquidity.value
        }
    }));
};

// Watch for changes and emit
watch([totalLiquidity, sellSideLiquidity, showSellSideOnly], () => {
    emitLiquidityUpdate();
});

const fetchMarkets = async () => {
    error.value = null;
    try {
        const response = await axios.get(`/api/tokens/${props.mint}/markets`);
        markets.value = response.data;
        emitLiquidityUpdate();
    } catch (err) {
        console.error('Error fetching token markets:', err);
        error.value = 'Failed to load markets.';
    }
};

onMounted(() => {
    loadLiquidityPreference();
    isLoading.value = true;
    fetchMarkets().finally(() => isLoading.value = false);
    intervalId = setInterval(fetchMarkets, 60000);
});

onUnmounted(() => {
    clearInterval(intervalId);
});
</script>

<style scoped>
.token-markets-container {
    max-height: 400px;
    overflow-y: auto;
}

/* Liquidity Toggle Row */
.liquidity-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
    background: rgba(139, 92, 246, 0.03);
}

.liquidity-summary {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.summary-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--compass-text-secondary, #7d8590);
}

.summary-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
}

.toggle-container {
    display: flex;
    gap: 0.15rem;
    background: rgba(139, 92, 246, 0.08);
    border-radius: 6px;
    padding: 0.15rem;
}

.toggle-btn {
    padding: 0.25rem 0.6rem;
    border: none;
    background: transparent;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.65rem;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.toggle-btn:hover:not(.active) {
    color: var(--compass-text-primary, #e6edf3);
    background: rgba(139, 92, 246, 0.1);
}

.toggle-btn.active {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #ffffff;
}

/* States */
.loading-state,
.error-state,
.empty-state {
    padding: 2rem;
    text-align: center;
    color: var(--compass-text-secondary, #7d8590);
}

.loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-top-color: var(--compass-purple, #8b5cf6);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 0.5rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.error-state {
    color: var(--compass-danger, #ef4444);
}

/* Table */
.markets-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.markets-table th {
    padding: 0.6rem 0.5rem;
    text-align: left;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--compass-text-secondary, #7d8590);
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(236, 72, 153, 0.03));
}

.markets-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: color 0.15s ease;
}

.markets-table th.sortable:hover {
    color: var(--compass-text-primary, #e6edf3);
}

.sort-icon {
    font-size: 0.5rem;
    margin-left: 0.25rem;
    opacity: 0.8;
}

.markets-table td {
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.1));
    color: var(--compass-text-primary, #e6edf3);
}

.markets-table tbody tr:hover {
    background: rgba(139, 92, 246, 0.05);
}

/* Exchange Column */
.col-exchange {
    width: 110px;
}

.exchange-cell {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.exchange-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dot-purple { background: var(--compass-purple, #8b5cf6); }
.dot-green { background: var(--compass-success, #22c55e); }
.dot-cyan { background: #10f7cf; }
.dot-blue { background: #3b82f6; }
.dot-teal { background: #14b8a6; }
.dot-orange { background: #f97316; }
.dot-gray { background: #6b7280; }

.exchange-name {
    font-size: 0.75rem;
    font-weight: 400;
    white-space: nowrap;
    color: var(--compass-text-secondary, #7d8590);
}

/* Pair Column */
.col-pair {
    width: 130px;
}

.pair-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.pair-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--compass-text-primary, #e6edf3);
    text-decoration: none;
    font-size: 0.75rem;
    font-weight: 400;
}

.pair-link:hover {
    color: var(--compass-purple, #8b5cf6);
}

.external-icon {
    width: 10px;
    height: 10px;
    opacity: 0.5;
    flex-shrink: 0;
}

.pair-link:hover .external-icon {
    opacity: 1;
}

.pair-type {
    flex-shrink: 0;
    font-size: 0.55rem;
    padding: 0.1rem 0.3rem;
    background: rgba(139, 92, 246, 0.15);
    color: var(--compass-purple, #8b5cf6);
    border-radius: 3px;
    text-transform: uppercase;
    font-weight: 500;
}

/* Metric Cells (Liquidity & Volume) */
.col-liquidity,
.col-volume {
    width: 140px;
}

.metric-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.metric-bar-bg {
    flex: 1;
    height: 5px;
    background: rgba(139, 92, 246, 0.1);
    border-radius: 3px;
    overflow: hidden;
}

.metric-bar-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
}

.liquidity-fill {
    background: linear-gradient(90deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
}

.volume-fill {
    background: linear-gradient(90deg, #10f7cf, #06b6d4);
}

.metric-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.75rem;
    font-weight: 500;
    min-width: 50px;
    text-align: right;
}

.liquidity-value {
    background: linear-gradient(90deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.volume-value {
    background: linear-gradient(90deg, #10f7cf, #06b6d4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Efficiency Column */
.col-efficiency {
    width: 65px;
    text-align: right;
}

.efficiency-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    font-weight: 600;
}

.eff-high {
    color: var(--compass-success, #22c55e);
}

.eff-medium {
    color: var(--compass-text-primary, #e6edf3);
}

.eff-low {
    color: var(--compass-text-secondary, #7d8590);
}

/* Footer */
.markets-footer {
    padding: 0.5rem;
    text-align: center;
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.1));
}

/* Light mode */
body:not(.dark-mode) .markets-table td {
    color: #1f2937;
}

body:not(.dark-mode) .pair-link {
    color: #1f2937;
}

body:not(.dark-mode) .exchange-name {
    color: #6b7280;
}

body:not(.dark-mode) .summary-value {
    color: #1f2937;
}

body:not(.dark-mode) .liquidity-toggle-row {
    background: rgba(139, 92, 246, 0.02);
}

body:not(.dark-mode) .toggle-container {
    background: rgba(139, 92, 246, 0.05);
}

body:not(.dark-mode) .toggle-btn {
    color: #6b7280;
}

body:not(.dark-mode) .toggle-btn:hover:not(.active) {
    color: #374151;
}

body:not(.dark-mode) .toggle-btn.active {
    color: #ffffff;
}

body:not(.dark-mode) .markets-table th.sortable:hover {
    color: #374151;
}

body:not(.dark-mode) .eff-medium {
    color: #1f2937;
}
</style>
